import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import {fetchSchoolListForDropdown} from "common-services";
import React, {useCallback, useEffect, useState} from "react";
import {FiChevronLeft, FiChevronRight, FiRefreshCw, FiSearch} from "react-icons/fi";
import {searchUser} from "../UserSetup/Services";
import {searchActivities} from "./Services";

const INITIAL_FILTERS = {schoolId: "", email: "", mobileNumber: ""};

const getActivityList = (data) =>
    data?.content || data?.activityList || data?.activities || data?.items || [];

const getUserList = (data) => data?.content || data?.userList || data?.users || [];

const getTotalElements = (data, rows) =>
    data?.totalElements ?? data?.totalItems ?? data?.total ?? rows.length;

const getTotalPages = (data, totalElements, size) =>
    data?.totalPages ?? Math.max(1, Math.ceil(totalElements / size));

const getValue = (activity, keys, fallback = "-") => {
    const key = keys.find((item) => activity?.[item] !== undefined && activity?.[item] !== null && activity?.[item] !== "");
    return key ? activity[key] : fallback;
};

const formatActivityDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const formattedDate = date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

    return `${formattedDate} ${formattedTime}`;
};

function ActivityLog() {
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    const [schools, setSchools] = useState([]);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [activities, setActivities] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadSchools = async () => {
            try {
                const response = await fetchSchoolListForDropdown();
                setSchools(response?.data || []);
            } catch (error) {
                toast({
                    title: "Unable to load schools",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
        };

        loadSchools();
    }, [toast]);

    const resolveUserId = useCallback(async (activeFilters) => {
        if (!activeFilters.email.trim() && !activeFilters.mobileNumber.trim()) return null;

        const request = {
            schoolId: activeFilters.schoolId ? Number(activeFilters.schoolId) : null,
            email: activeFilters.email.trim() || null,
            mobileNumber: activeFilters.mobileNumber.trim() || null,
        };
        const response = await searchUser(request, 0, 20);
        const users = getUserList(response?.data);
        const email = activeFilters.email.trim().toLowerCase();
        const mobile = activeFilters.mobileNumber.trim();
        const user = users.find((item) =>
            (!email || item?.email?.toLowerCase() === email) &&
            (!mobile || String(item?.mobileNumber || item?.phone || "") === mobile)
        );

        return user?.id || user?.userId || null;
    }, []);

    const fetchActivities = useCallback(async (activeFilters, targetPage, targetSize) => {
        setLoading(true);
        try {
            const userId = await resolveUserId(activeFilters);
            if ((activeFilters.email.trim() || activeFilters.mobileNumber.trim()) && !userId) {
                setActivities([]);
                setTotalElements(0);
                setTotalPages(1);
                return;
            }

            const response = await searchActivities({
                userId,
                schoolId: activeFilters.schoolId ? Number(activeFilters.schoolId) : null,
                page: targetPage,
                size: targetSize,
            });
            const rows = getActivityList(response?.data);
            const count = getTotalElements(response?.data, rows);
            setActivities(rows);
            setTotalElements(count);
            setTotalPages(getTotalPages(response?.data, count, targetSize));
        } catch (error) {
            setActivities([]);
            setTotalElements(0);
            setTotalPages(1);
            toast({
                title: "Unable to fetch activity logs",
                description: error?.message || error?.errorMessage || "Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [resolveUserId, toast]);

    useEffect(() => {
        fetchActivities(appliedFilters, page, size);
    }, [appliedFilters, fetchActivities, page, size]);

    const handleFilterChange = (event) => {
        const {name, value} = event.target;
        setFilters((current) => ({...current, [name]: value}));
    };

    const handleSearch = (event) => {
        event.preventDefault();
        setPage(0);
        setAppliedFilters({...filters});
    };

    const handleReset = () => {
        setFilters(INITIAL_FILTERS);
        setPage(0);
        setAppliedFilters(INITIAL_FILTERS);
    };

    return (
        <Flex direction="column" pt={{base: "120px", md: "75px"}} gap="20px">
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap="12px">
                <Box>
                    <Heading as="h1" size="lg" color={textColor}>Activity Log</Heading>
                    <Text color={mutedColor} mt="4px">Review recent user activity across schools.</Text>
                </Box>
                <Button
                    leftIcon={<FiRefreshCw/>}
                    onClick={() => fetchActivities(appliedFilters, page, size)}
                    isLoading={loading}
                    variant="outline"
                >
                    Refresh
                </Button>
            </Flex>

            <Card>
                <CardHeader pb="16px">
                    <Text fontWeight="semibold" color={textColor}>Search filters</Text>
                </CardHeader>
                <CardBody>
                    <Box as="form" onSubmit={handleSearch} width="100%">
                        <SimpleGrid columns={{base: 1, md: 2, xl: 4}} spacing="16px">
                            <FormControl>
                                <FormLabel fontSize="sm">School</FormLabel>
                                <Select name="schoolId" value={filters.schoolId} onChange={handleFilterChange} placeholder="All schools">
                                    {schools.map((school) => (
                                        <option key={school.value} value={school.value}>{school.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Email</FormLabel>
                                <Input name="email" type="email" value={filters.email} onChange={handleFilterChange} placeholder="Optional email"/>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Mobile number</FormLabel>
                                <Input name="mobileNumber" type="tel" value={filters.mobileNumber} onChange={handleFilterChange} placeholder="Optional mobile"/>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Rows per page</FormLabel>
                                <Select value={size} onChange={(event) => { setPage(0); setSize(Number(event.target.value)); }}>
                                    {[10, 20, 50, 100].map((value) => <option key={value} value={value}>{value}</option>)}
                                </Select>
                            </FormControl>
                        </SimpleGrid>
                        <HStack mt="20px" spacing="12px">
                            <Button type="submit" colorScheme="teal" leftIcon={<FiSearch/>} isLoading={loading}>Search</Button>
                            <Button type="button" variant="ghost" onClick={handleReset}>Clear filters</Button>
                        </HStack>
                    </Box>
                </CardBody>
            </Card>

            <Card overflowX="auto">
                <CardHeader pb="16px">
                    <Flex justify="space-between" align="center" width="100%">
                        <Text fontWeight="semibold" color={textColor}>Recent activity</Text>
                        <Badge colorScheme="teal" px="10px" py="4px" borderRadius="full">{totalElements} records</Badge>
                    </Flex>
                </CardHeader>
                <CardBody>
                    <Box width="100%" overflowX="auto">
                        <Table variant="simple" minWidth="900px">
                            <Thead>
                                <Tr>
                                    <Th>Date & time</Th>
                                    <Th>School</Th>
                                    <Th>User</Th>
                                    <Th>Contact</Th>
                                    <Th>Activity</Th>
                                    <Th>IP Address</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {!loading && activities.map((activity, index) => (
                                    <Tr key={getValue(activity, ["id", "activityId"], `${page}-${index}`)}>
                                        <Td whiteSpace="nowrap">{formatActivityDate(getValue(activity, ["activityDate", "createdAt", "date", "timestamp"], ""))}</Td>
                                        <Td fontWeight="medium">{getValue(activity, ["schoolName", "school"])}</Td>
                                        <Td>{getValue(activity, ["userName", "fullName", "username", "user"])}</Td>
                                        <Td>
                                            <Text fontSize="sm">{getValue(activity, ["email", "userEmail"])}</Text>
                                            <Text fontSize="xs" color={mutedColor}>{getValue(activity, ["mobileNumber", "phone", "userMobile"])}</Text>
                                        </Td>
                                        <Td><Badge colorScheme="blue">{getValue(activity, ["activityType", "action", "type", "activity"])}</Badge></Td>
                                        <Td maxWidth="320px">{getValue(activity, ["location", "details", "message", "remarks"])}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                        {loading && <Flex justify="center" py="50px"><Spinner color="teal.400" size="lg"/></Flex>}
                        {!loading && activities.length === 0 && (
                            <Flex direction="column" align="center" py="50px">
                                <Heading size="sm" color={textColor}>No activity logs found</Heading>
                                <Text color={mutedColor} mt="6px">Try changing or clearing the filters.</Text>
                            </Flex>
                        )}
                    </Box>
                </CardBody>
                <Flex justify="space-between" align="center" px="22px" pb="22px" gap="12px" wrap="wrap">
                    <Text color={mutedColor} fontSize="sm">
                        Page {page + 1} of {totalPages}
                    </Text>
                    <HStack>
                        <Button
                            leftIcon={<FiChevronLeft/>}
                            onClick={() => setPage((current) => Math.max(0, current - 1))}
                            isDisabled={loading || page === 0}
                            size="sm"
                        >
                            Previous
                        </Button>
                        <Button
                            rightIcon={<FiChevronRight/>}
                            onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                            isDisabled={loading || page + 1 >= totalPages}
                            size="sm"
                        >
                            Next
                        </Button>
                    </HStack>
                </Flex>
            </Card>
        </Flex>
    );
}

export default ActivityLog;
